import { useMutation, useQueryClient } from "@tanstack/react-query";
import { ResponsiveNotePhotoMappingStatus } from "@rebuild/shared/models";
import type { ResponsiveNotePhoto } from "@rebuild/shared/models";
import { getRebuildAPI } from "@rebuild/shared/api";
import { RESPONSIVE_NOTE_PHOTO_KEYS } from "@/hooks/queries/responsive-note-photos/queryKeys";
import { getInternalOrgOverride } from "@/redux/actions/app.actions";

const { estimatesControllerReassignResponsiveNotePhotos } = getRebuildAPI();

interface ReassignResponsiveNotePhotosMutationParams {
  estimateId: string;
  photos: { noteId: string; photoId: string }[];
  targetRoomId: string;
}

export function useReassignResponsiveNotePhotosMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: ReassignResponsiveNotePhotosMutationParams) => {
      const orgOverride = getInternalOrgOverride();
      const response = await estimatesControllerReassignResponsiveNotePhotos(
        params.estimateId,
        {
          photos: params.photos,
          targetRoomId: params.targetRoomId,
        },
        orgOverride ? { overrideOrganizationId: orgOverride.organizationId } : undefined,
      );

      if (!response.success) {
        throw new Error("Failed to reassign photos");
      }

      return response.data;
    },
    onMutate: async (variables) => {
      await queryClient.cancelQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      });

      const previousPhotos = queryClient.getQueryData<ResponsiveNotePhoto[]>(
        RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      );

      if (previousPhotos) {
        const photoKeys = new Set(variables.photos.map((photo) => `${photo.noteId}:${photo.photoId}`));
        const nextPhotos = previousPhotos.map((photo) => {
          if (!photoKeys.has(`${photo.noteId}:${photo.id}`)) {
            return photo;
          }

          return {
            ...photo,
            scopeNoteRoomId: variables.targetRoomId,
            mappingStatus: ResponsiveNotePhotoMappingStatus.mapped,
          };
        });
        queryClient.setQueryData(RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId), nextPhotos);
      }

      return { previousPhotos };
    },
    onError: (_error, variables, context) => {
      if (context?.previousPhotos) {
        queryClient.setQueryData(RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId), context.previousPhotos);
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: RESPONSIVE_NOTE_PHOTO_KEYS.estimate(variables.estimateId),
      });
    },
  });
}
